/**
 * The sign-up road's measurement contract (meta #321, Phase 2): one GA4 event
 * per step, each carrying `entry` (the door she came in by), `step` and
 * `signedIn`, so path A and path B can be split in one funnel.
 */
import { trackGtagEvent } from '@/utils/Analytics';
import { isInternalTraffic } from '@/utils/internalTraffic';
import { getItemWithTTL, setItemWithTTL, TTL } from '@/utils/localStorageWithTTL';
import { isPrerendering } from '@/utils/prerender';

export type SignupEntry = 'home' | 'try' | 'q' | 'blog' | 'state' | 'ad' | 'email' | 'other';

export type SignupRoadEvent =
  | 'diagnostic_started'
  | 'diagnostic_completed'
  | 'diagnostic_capture_viewed'
  | 'diagnostic_email_captured'
  | 'diagnostic_turn_completed'
  | 'diagnostic_end_viewed'
  | 'keep_it_shown'
  | 'keep_it_submitted'
  | 'keep_it_sent'
  | 'keep_it_provider'
  | 'aim_shown'
  | 'aim_done'
  | 'offer_shown';

export const SIGNUP_ENTRY_KEY = 'swtpa_signup_entry_v1';

// fbclid is left out on purpose: Facebook appends it to organic link clicks too.
const AD_CLICK_IDS = ['gclid', 'wbraid', 'gbraid', 'rdt_cid'] as const;
const PAID_MEDIUMS = new Set(['cpc', 'ppc', 'paid', 'paid_social', 'paidsocial', 'display']);

/** The door, from the landing URL. The site's own CTAs tag blog (`utm_source=blog`) and state pages (`utm_source=licensing`); /q forwards `prefill`. */
export function detectSignupEntry(pathname: string, search: string): SignupEntry {
  const p = new URLSearchParams(search);
  if (p.get('prefill')) return 'q';
  const source = p.get('utm_source')?.toLowerCase();
  const medium = p.get('utm_medium')?.toLowerCase();
  if (AD_CLICK_IDS.some((k) => p.get(k)) || (medium && PAID_MEDIUMS.has(medium))) return 'ad';
  if (source === 'blog') return 'blog';
  if (source === 'licensing') return 'state';
  if (medium === 'email' || source === 'email' || source === 'substack') return 'email';
  if (source) return 'other';
  if (pathname === '/') return 'home';
  if (pathname === '/try') return 'try';
  return 'other';
}

function currentEntry(): SignupEntry {
  return detectSignupEntry(window.location.pathname, window.location.search);
}

/** Stamp the door for this run; later steps (end screen, keep-it, offer) happen on other URLs. */
export function rememberSignupEntry(entry: SignupEntry): void {
  try {
    setItemWithTTL(SIGNUP_ENTRY_KEY, entry, TTL.THREE_MONTHS);
  } catch {
    // private mode: signupEntry() falls back to the current URL
  }
}

export function signupEntry(): SignupEntry {
  try {
    return getItemWithTTL<SignupEntry>(SIGNUP_ENTRY_KEY) ?? currentEntry();
  } catch {
    return currentEntry();
  }
}

export function trackSignupStep(
  event: SignupRoadEvent,
  step: string,
  signedIn: boolean,
  extra: Record<string, unknown> = {}
): void {
  if (isPrerendering() || isInternalTraffic()) return;
  trackGtagEvent(event, { ...extra, entry: signupEntry(), step, signedIn });
}

export const trackTurnCompleted = (turn: number, signedIn: boolean, extra: Record<string, unknown> = {}) =>
  trackSignupStep('diagnostic_turn_completed', `turn_${turn}`, signedIn, { ...extra, turn });
export const trackEndViewed = (signedIn: boolean) => trackSignupStep('diagnostic_end_viewed', 'end', signedIn);

// Keep-it and aim screens are not built yet (Phase 2 PRs 3 and 4 wire these).
export const trackKeepItShown = (signedIn: boolean) => trackSignupStep('keep_it_shown', 'keep_it', signedIn);
export const trackKeepItSubmitted = (signedIn: boolean) => trackSignupStep('keep_it_submitted', 'keep_it_submit', signedIn);
export const trackKeepItSent = (signedIn: boolean, existing: boolean) =>
  trackSignupStep('keep_it_sent', 'keep_it_sent', signedIn, { method: 'email', existing });
export const trackKeepItProvider = (signedIn: boolean, provider: 'apple' | 'google') =>
  trackSignupStep('keep_it_provider', 'keep_it_provider', signedIn, { method: provider });
export const trackAimShown = (signedIn: boolean) => trackSignupStep('aim_shown', 'aim', signedIn);
export const trackAimDone = (signedIn: boolean, examType: string, hasDate: boolean) =>
  trackSignupStep('aim_done', 'aim_done', signedIn, { examType, hasDate });

export const trackOfferShown = (surface: 'pricing' | 'trial_ended', signedIn: boolean) =>
  trackSignupStep('offer_shown', 'offer', signedIn, { surface });
